import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { Loader2, ArrowLeft, PenSquare } from 'lucide-react';
import { useAdminAuth } from '../ControlTower/auth/useAdminAuth';
import { MFAFlow } from '../ControlTower/auth/MFAFlow';
import { supabase } from '../../lib/supabase';
import { AdminLoginScreen } from '../ControlTower/AdminLoginScreen';
import { BlogList } from './BlogList';
import { BlogEditor } from './BlogEditor';

/**
 * Blog admin — platform-admin-only self-service editor for marketing blog
 * posts. Gated against public.platform_admins (same table as ControlTower).
 *
 * Runs in two places from the same component:
 *   - app domain at /admin/blog        (basePath="/admin/blog")
 *   - blog.forgedash.uk at the root     (basePath="")
 *
 * When there is no session it renders the shared admin login (Google OAuth),
 * so the dedicated subdomain is self-contained. Fully isolated from financial
 * modules: only touches blog_* tables + the blog-media storage bucket.
 */

export function BlogAdmin({ basePath = '/admin/blog' }: { basePath?: string } = {}) {
  const { session, isAdmin, assured, loading, refresh } = useAdminAuth();
  const location = useLocation();
  const listPath = basePath || '/';
  const inEditor = location.pathname !== listPath;

  if (loading) {
    return (
      <div className="h-screen w-screen bg-canvas flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gold" />
      </div>
    );
  }

  // Not signed in / not an admin → shared admin login (Google OAuth).
  if (!session) return <AdminLoginScreen isUnauthorized={false} />;
  if (!isAdmin) return <AdminLoginScreen isUnauthorized={true} />;
  if (!assured) return <MFAFlow key={session.user.id} onSuccess={refresh} />;

  return (
    <div className="min-h-screen bg-canvas text-fg-2">
      <header className="border-b border-line bg-panel/60 backdrop-blur">
        <div className="mx-auto flex max-w-[1100px] items-center justify-between px-6 h-16">
          <div className="flex items-center gap-3">
            {inEditor ? (
              <Link
                to={listPath}
                className="inline-flex items-center gap-2 text-sm font-medium text-fg-3 hover:text-fg"
              >
                <ArrowLeft className="h-4 w-4" />
                All posts
              </Link>
            ) : (
              <span className="inline-flex items-center gap-2 font-display text-lg font-bold text-fg">
                <PenSquare className="h-5 w-5 text-gold" />
                ForgeDash Blog
              </span>
            )}
          </div>
          <button
            onClick={() => supabase.auth.signOut().then(() => window.location.reload())}
            className="text-sm text-fg-3 hover:text-fg"
          >
            Sign out
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-[1100px] px-6 py-10">
        <Routes>
          <Route path={listPath} element={<BlogList basePath={basePath} />} />
          <Route path={`${basePath}/new`} element={<BlogEditor basePath={basePath} />} />
          <Route path={`${basePath}/:id`} element={<BlogEditor basePath={basePath} />} />
        </Routes>
      </main>
    </div>
  );
}
